import { useEffect, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Check, ChevronDown, Plus, Tent } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { inputClass } from "@/lib/styleClasses"
import { useAdminArealStore } from "@/store/adminArealStore"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"

const toSlug = (s: string) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")

interface CreateArealForm {
    name: string
    slug: string
    address: string
    city: string
    country: string
    spotCount: number
    currency: string
}

const CreateArealModal = ({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) => {
    const { t } = useTranslation()
    const { register, handleSubmit, watch, setValue, formState: { isSubmitting } } = useForm<CreateArealForm>({
        defaultValues: { country: "CZ", currency: "CZK", spotCount: 10 },
    })

    const nameValue = watch("name")
    useEffect(() => {
        setValue("slug", toSlug(nameValue ?? ""))
    }, [nameValue, setValue])

    const onSubmit = async (data: CreateArealForm) => {
        try {
            const areal = await api.post<ArealResponse>("/areals", {
                ...data,
                spotCount: Number(data.spotCount),
            })
            toast.success(t("layout.admin.arealSelector.createToastSuccess"))
            onCreated(areal.id)
        } catch {
            toast.error(t("layout.admin.arealSelector.createToastFailed"))
        }
    }

    const labelCls = "block text-xs font-black uppercase tracking-widest text-slate-600 mb-1"

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[40px] shadow-2xl border border-slate-100 w-full max-w-md p-8">
                    <h2 className="text-xl font-black text-slate-800 mb-6">{t("layout.admin.arealSelector.createTitle")}</h2>

                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                        <div>
                            <label className={labelCls}>{t("layout.admin.arealSelector.fieldName")}</label>
                            <input {...register("name", { required: true })} className={inputClass} />
                        </div>

                        <div>
                            <label className={labelCls}>{t("layout.admin.arealSelector.fieldSlug")}</label>
                            <input {...register("slug", { required: true })} className={inputClass} />
                            <p className="text-[11px] text-slate-400 font-bold mt-1">{t("layout.admin.arealSelector.fieldSlugHint")}</p>
                        </div>

                        <div>
                            <label className={labelCls}>{t("layout.admin.arealSelector.fieldAddress")}</label>
                            <input {...register("address", { required: true })} className={inputClass} />
                        </div>

                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className={labelCls}>{t("layout.admin.arealSelector.fieldCity")}</label>
                                <input {...register("city", { required: true })} className={inputClass} />
                            </div>
                            <div className="w-24">
                                <label className={labelCls}>{t("layout.admin.arealSelector.fieldCountry")}</label>
                                <input {...register("country", { required: true })} maxLength={2} className={inputClass} />
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className={labelCls}>{t("layout.admin.arealSelector.fieldSpotCount")}</label>
                                <input {...register("spotCount", { required: true, min: 1 })} type="number" min={1} className={inputClass} />
                            </div>
                            <div className="w-32">
                                <label className={labelCls}>{t("layout.admin.arealSelector.fieldCurrency")}</label>
                                <input {...register("currency", { required: true })} maxLength={3} className={inputClass} />
                            </div>
                        </div>

                        <div className="flex gap-3 mt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-slate-100 text-slate-600 font-bold rounded-2xl py-2.5 hover:bg-slate-200 transition-colors duration-200"
                            >
                                {t("layout.admin.arealSelector.createCancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl py-2.5 hover:bg-brand-dark active:scale-95 transition-all duration-200 disabled:opacity-60"
                            >
                                {isSubmitting
                                    ? t("layout.admin.arealSelector.createSaving")
                                    : t("layout.admin.arealSelector.createSave")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

export const ArealSelectorDropdown = () => {
    const { t } = useTranslation()
    const { selectedArealId, setSelectedArealId } = useAdminArealStore()
    const [open, setOpen] = useState(false)
    const [showCreate, setShowCreate] = useState(false)
    const queryClient = useQueryClient()

    const { data: areals = [], isLoading } = useQuery({
        queryKey: ["admin", "areals"],
        queryFn: () => api.get<ArealResponse[]>("/areals"),
        staleTime: 10 * 60 * 1000,
    })

    // Auto-select: pick stored ID if valid, otherwise fall back to first
    useEffect(() => {
        if (areals.length === 0) return
        const valid = areals.some((a) => a.id === selectedArealId)
        if (!valid) {
            setSelectedArealId(areals[0]!.id)
        }
    }, [areals, selectedArealId, setSelectedArealId])

    const selected = areals.find((a) => a.id === selectedArealId) ?? areals[0]

    const handleCreated = async (id: string) => {
        await queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        setSelectedArealId(id)
        setShowCreate(false)
    }

    const createButton = (
        <button
            type="button"
            onClick={() => { setOpen(false); setShowCreate(true) }}
            className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand hover:text-brand-dark transition-colors duration-150 px-1 py-0.5"
        >
            <Plus className="size-3.5" />
            {t("layout.admin.arealSelector.createNew")}
        </button>
    )

    if (isLoading) {
        return <div className="h-10 animate-pulse rounded-2xl bg-slate-100" />
    }

    return (
        <>
            {showCreate && (
                <CreateArealModal
                    onClose={() => setShowCreate(false)}
                    onCreated={handleCreated}
                />
            )}

            {areals.length === 0 && (
                <div className="flex flex-col gap-2 px-3 py-2.5">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-400">
                        <Tent className="size-4 shrink-0" />
                        {t("layout.admin.arealSelector.noAreals")}
                    </div>
                    {createButton}
                </div>
            )}

            {areals.length === 1 && (
                <div className="flex flex-col gap-1 px-3 py-2.5">
                    <div className="flex items-center gap-2 text-sm font-black text-slate-700">
                        <Tent className="size-4 shrink-0 text-brand" />
                        <span className="truncate">{selected?.name}</span>
                    </div>
                    {createButton}
                </div>
            )}

            {areals.length >= 2 && (
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="flex w-full items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm font-bold text-slate-800 transition-all duration-200 hover:bg-slate-100"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <Tent className="size-4 shrink-0 text-brand" />
                            <span className="truncate">{selected?.name}</span>
                        </div>
                        <ChevronDown className={cn("size-4 shrink-0 text-slate-400 transition-transform duration-200", open && "rotate-180")} />
                    </button>

                    {open && (
                        <>
                            {/* Backdrop */}
                            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                            {/* Panel */}
                            <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-2xl border border-slate-100 bg-white p-1 shadow-xl">
                                {areals.map((areal) => {
                                    const isActive = areal.id === selected?.id
                                    return (
                                        <button
                                            key={areal.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedArealId(areal.id)
                                                setOpen(false)
                                            }}
                                            className={cn(
                                                "flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-bold transition-colors duration-150",
                                                isActive
                                                    ? "bg-charcoal text-white"
                                                    : "text-slate-600 hover:bg-slate-50",
                                            )}
                                        >
                                            <span className="truncate">{areal.name}</span>
                                            {isActive && <Check className="size-3.5 shrink-0 text-brand" />}
                                        </button>
                                    )
                                })}
                                <div className="border-t border-slate-100 mt-1 pt-1 px-2 pb-1">
                                    {createButton}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    )
}
